import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAuthHeaders } from "@/api/client";
import { useSession } from "@/services/auth0/session";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";
import type { CenterIntakeObligationPage } from "@/types/center-intake-obligation";

export function useCenterIntakeObligations(
  page: number,
  search = "",
  view: "outstanding" | "received" | "returning" | "history" = "outstanding",
  filter?: { centerId?: string; operatorId?: string }
) {
  const { token } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: [
      "carrier",
      "center-intake-obligations",
      dispatchCenterId,
      page,
      search,
      view,
      ...(filter ? [filter] : []),
    ],
    enabled: !!token,
    refetchOnWindowFocus: true,
    queryFn: async () =>
      (
        await api.get<CenterIntakeObligationPage>(
          "/center-intake-obligations",
          {
            headers: getAuthHeaders(token),
            params: {
              page,
              search,
              view,
              ...(filter?.centerId || dispatchCenterId
                ? { dispatchCenterId: filter?.centerId ?? dispatchCenterId }
                : {}),
              ...(filter?.operatorId ? { operatorId: filter.operatorId } : {}),
            },
          }
        )
      ).data,
  });
}
export function useReceiveCenterIntakeObligation() {
  const { token } = useSession();
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string;
      requestId: string;
      expectedVersion: number;
      packageIds: string[];
    }) =>
      (
        await api.post<{ data: { timing: string } }>(
          `/center-intake-obligations/${id}/receive`,
          input,
          { headers: getAuthHeaders(token) }
        )
      ).data.data,
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["carrier", "center-intake-obligations"],
        }),
        client.invalidateQueries({ queryKey: ["carrier", "tasks"] }),
        client.invalidateQueries({ queryKey: ["carrier", "task-backlog"] }),
      ]);
    },
  });
}

export interface BatchReceiptInput {
  centerId: string;
  items: { id: string; expectedVersion: number; packageIds: string[] }[];
}
export function useReceiveCenterIntakeObligationBatch() {
  const { token } = useSession();
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: BatchReceiptInput) =>
      (
        await api.post<{
          data: {
            changed: boolean;
            receipts: { id: string; timing: string }[];
          };
        }>("/center-intake-obligations/receive", input, {
          headers: getAuthHeaders(token),
        })
      ).data.data,
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["carrier", "center-intake-obligations"],
        }),
        client.invalidateQueries({ queryKey: ["carrier", "tasks"] }),
        client.invalidateQueries({ queryKey: ["carrier", "task-backlog"] }),
      ]);
    },
  });
}
export interface IntakeObligationDiscrepancyInput {
  id: string;
  requestId: string;
  centerId: string;
  condition: "MISSING" | "DAMAGED";
  note: string;
}
export function useReportIntakeObligationDiscrepancy() {
  const { token } = useSession();
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async ({ id, ...input }: IntakeObligationDiscrepancyInput) =>
      (
        await api.post(
          `/center-intake-obligations/${id}/discrepancies`,
          input,
          {
            headers: getAuthHeaders(token),
          }
        )
      ).data,
    onSettled: async () => {
      await client.invalidateQueries({
        queryKey: ["carrier", "center-intake-obligations"],
      });
    },
  });
}
